import dayjs from 'dayjs';
import 'shared/dateutil';
import { toHumanTime } from 'shared/dateutil';
import { IcoCheckCircle, IcoClock } from '@components/icons';
import { Lesson, State } from '@course/components/module-helpers';
import { useIntl } from 'shared/intl/use-intl';
import { IntlFn } from 'shared/intl/types';
import { URLS } from 'shared/urls';
import { SprintLesson, SprintWeek } from 'shared/sprint-week';
import { CelebrationTier } from './celebration';
import { getCurrentCourse } from './state';

/** The completion gate's clock, so the deadline reads the same for everyone. */
const GATE_TIMEZONE = 'America/Los_Angeles';

const CONFETTI_COLORS = ['#0ea5e9', '#f59e0b', '#22c55e', '#ec4899', '#8b5cf6'];

function Confetti({ pieces }: { pieces: number }) {
  return (
    <span aria-hidden="true" class="pointer-events-none absolute left-1/2 top-8">
      {Array.from({ length: pieces }, (_, i) => {
        const angle = (i / pieces) * Math.PI * 2;
        const distance = 70 + (i % 3) * 30;
        return (
          <span
            key={i}
            class="an-confetti-piece absolute block w-2 h-3 rounded-sm"
            style={{
              background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              '--an-x': `${Math.round(Math.cos(angle) * distance)}px`,
              '--an-y': `${Math.round(Math.sin(angle) * distance + 40)}px`,
              '--an-r': `${(i % 2 ? 1 : -1) * (180 + i * 20)}deg`,
            }}
          />
        );
      })}
    </span>
  );
}

/** What the sprint card needs of a course: its links, and whether it has standups. */
type SprintCourse = {
  id: UUID;
  title: string;
  isProduct?: boolean;
  checkinsEnabled?: boolean;
};

function lessonHref(course: SprintCourse, lesson: Pick<SprintLesson, 'id' | 'title'>) {
  return URLS.student.lesson({ course, lesson });
}

function LessonLinks({ course, lessons }: { course: SprintCourse; lessons: SprintLesson[] }) {
  return (
    <ul class="flex flex-col gap-1">
      {lessons.map((lesson) => (
        <li key={lesson.id}>
          <a class="underline" href={lessonHref(course, lesson)}>
            {lesson.title}
          </a>
        </li>
      ))}
    </ul>
  );
}

export function celebrationHeading(
  intl: IntlFn,
  tier: CelebrationTier,
  { state, lesson }: { state: State; lesson: Lesson },
) {
  if (tier === 'module') {
    return intl('{title:string} complete', { title: state.modules[lesson.moduleId]?.title ?? '' });
  }
  if (tier === 'course') {
    return intl('{title:string} complete', { title: getCurrentCourse(state).title });
  }
  if (tier === 'week') {
    return intl("This week's lessons complete");
  }
  if (tier === 'on_track') {
    return intl("You're on track this week");
  }
  return intl('{title:string} done', { title: lesson.title });
}

/**
 * The moment right after a student marks a lesson complete. Sized to what the
 * click finished; see `celebrationTier`.
 */
export function CelebrationPanel({
  tier,
  lesson,
  state,
  nextOpensAt,
}: {
  tier: CelebrationTier;
  lesson: Lesson;
  state: State;
  nextOpensAt?: Date;
}) {
  const intl = useIntl();
  const week = state.sprintWeek;
  const doneThisWeek = week?.lessons.filter((l) => l.status === 'done').length ?? 0;
  const progress = week?.lessons.length
    ? intl('{done:number} of {total:number} this week marked complete', {
        done: doneThisWeek,
        total: week.lessons.length,
      })
    : intl('{done:number} of {total:number} lessons complete', {
        done: state.completedLessons.length,
        total: state.orderedLessonIds.length,
      });
  const confetti = tier === 'lesson' ? 0 : tier === 'module' || tier === 'on_track' ? 14 : 24;

  return (
    <section
      class="relative overflow-hidden flex flex-col items-center gap-2 text-center p-6 mb-6 rounded-md border border-green-200 bg-green-50 dark:bg-green-950/40 dark:border-green-900 dark:text-gray-100"
      role="status"
    >
      {confetti > 0 && <Confetti pieces={confetti} />}
      <IcoCheckCircle class="an-check-pop size-12 text-green-500" />
      <h2 class="font-display text-2xl leading-snug text-gray-900 dark:text-white">
        {celebrationHeading(intl, tier, { state, lesson })}
      </h2>
      <p class="text-gray-600 dark:text-gray-300">{progress}</p>
      {nextOpensAt && (
        <p class="text-gray-600 dark:text-gray-300">
          {intl('Next lesson opens {date:string}', { date: toHumanTime(nextOpensAt) })}
        </p>
      )}
    </section>
  );
}

/**
 * This cycle of an immersion sprint: whether everything released is done, the
 * week's lessons, and the deadline. Shown on the course home page and after
 * marking a lesson complete.
 */
export function SprintWeekCard({
  course,
  week,
  showStandupLink = true,
}: {
  course: SprintCourse;
  week: SprintWeek;
  /** Off where the page already shows today's standup (the course home). */
  showStandupLink?: boolean;
}) {
  const intl = useIntl();
  // With the date: from Monday night of a gate's final week the deadline
  // shown is already past, and a bare weekday would read as next week's.
  const deadline = dayjs(week.deadlineAt).tz(GATE_TIMEZONE).format('dddd, MMM D, h:mm A');

  return (
    <section class="flex flex-col gap-4 p-6 mb-6 rounded-md border dark:border-gray-700 text-left dark:text-gray-200">
      {week.overdue.length === 0 ? (
        <p class="flex items-center gap-2 font-semibold text-green-700 dark:text-green-400">
          <IcoCheckCircle class="size-5 text-green-500" />
          {intl('On track: every released lesson marked complete')}
        </p>
      ) : (
        <div class="flex flex-col gap-2">
          <p class="font-semibold text-amber-700 dark:text-amber-400">
            {intl(
              '{count:number} released {count | pluralize lesson lessons} not marked complete',
              { count: week.overdue.length },
            )}
          </p>
          <LessonLinks course={course} lessons={week.overdue} />
        </div>
      )}

      {week.lessons.length > 0 && (
        <div class="flex flex-col gap-2">
          <header class="flex flex-wrap justify-between gap-x-4 text-sm">
            <strong>{intl('This week')}</strong>
            <span class="text-gray-500 dark:text-gray-400">
              {intl('Due {date:string} Pacific', { date: deadline })}
            </span>
          </header>
          <ul class="flex flex-col gap-2">
            {week.lessons.map((lesson) => (
              <li key={lesson.id} class="flex items-start gap-2">
                {lesson.status === 'done' && (
                  <IcoCheckCircle class="size-5 shrink-0 text-green-500" />
                )}
                {lesson.status === 'not_done' && (
                  <span class="size-5 shrink-0 p-0.5">
                    <span class="block size-4 rounded-full border-2 border-amber-500" />
                  </span>
                )}
                {lesson.status === 'upcoming' && <IcoClock class="size-5 shrink-0 text-gray-400" />}
                <span class="flex flex-col">
                  {lesson.status === 'upcoming' ? (
                    <span class="text-gray-500 dark:text-gray-400">{lesson.title}</span>
                  ) : (
                    <a href={lessonHref(course, lesson)} class="underline">
                      {lesson.title}
                    </a>
                  )}
                  {lesson.status === 'upcoming' && lesson.availableAt && (
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                      {intl('Opens {date:string}', {
                        date: dayjs(lesson.availableAt).format('ddd h:mm A'),
                      })}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showStandupLink && course.checkinsEnabled && (
        <a
          class="self-start underline"
          href={URLS.student.coursePage({ course, page: 'checkins' })}
        >
          {intl("Today's standup")}
        </a>
      )}
    </section>
  );
}

/**
 * Top-of-lesson reminder for released sprint lessons the student moved past
 * without marking complete.
 */
export function OverdueBanner({
  state,
  week,
  currentLessonId,
}: {
  state: State;
  week: SprintWeek;
  currentLessonId: UUID;
}) {
  const intl = useIntl();
  const earlierIds = new Set(
    state.orderedLessonIds.slice(0, Math.max(state.orderedLessonIds.indexOf(currentLessonId), 0)),
  );
  const others = week.overdue.filter((l) => earlierIds.has(l.id));
  if (!others.length) {
    return null;
  }
  return (
    <aside class="flex flex-col gap-2 p-4 mb-8 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900 dark:text-gray-100 font-sans">
      <p class="font-semibold">
        {intl('{count:number} earlier {count | pluralize lesson lessons} not marked complete', {
          count: others.length,
        })}
      </p>
      <LessonLinks course={getCurrentCourse(state)} lessons={others} />
    </aside>
  );
}
